// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCartCountSafe, getCart } from '../services/orderApi';
import { API_BASES } from '../config';

const API = axios.create({
  baseURL: `${API_BASES.auth}`,
  headers: { 'Content-Type': 'application/json' }
});



export const AuthContext = createContext({
  isAuthenticated: false,
  role: null,
  username: null,
  accessToken: null,
  refreshToken: null,
  isAuthLoading: true,
  cartItemCount: 0,
  login: async () => {},
  loginWithGoogle: async () => {},
  linkGoogle: async () => {},
  logout: () => {},
  refreshCartCount: async () => {}
});

const extractRole = decoded =>
  (decoded.role || decoded.rol || decoded.roles?.[0] || '').toString().toUpperCase();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    role: null,
    username: null,
    accessToken: null,
    refreshToken: null,
    isAuthLoading: true,
    cartItemCount: 0
  });

  const isRefreshing = useRef(false);
  const refreshQueue = useRef([]);

  // Anti-spam para count
  const lastCartFetch = useRef(0);
  const cartFetching  = useRef(false);
  const CART_TTL_MS   = 4000;   // throttle de 4s
  const lastCartValue = useRef(0);

  const subscribeTokenRefresh = (cb) => { refreshQueue.current.push(cb); };
  const onRefreshed = (newToken) => {
    refreshQueue.current.forEach(cb => cb(newToken));
    refreshQueue.current = [];
  };

  const setAuthHeader = (token) => {
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete axios.defaults.headers.common['Authorization'];
  };

  const persistTokens = (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  };

  const clearTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setAuthHeader(null);
  };

  const refreshCall = async (refreshToken) => {
    const { data } = await API.post('/refresh', { refreshToken });
    return data; // { accessToken, refreshToken? }
  };

  const tryRefreshToken = async (storedRefresh) => {
    if (!storedRefresh) return false;

    if (isRefreshing.current) {
      return new Promise(resolve => {
        subscribeTokenRefresh((newToken) => resolve(!!newToken));
      });
    }

    isRefreshing.current = true;
    try {
      const resp = await refreshCall(storedRefresh);
      const newAccess  = resp.accessToken;
      const newRefresh = resp.refreshToken || storedRefresh;

      persistTokens(newAccess, newRefresh);
      setAuthHeader(newAccess);

      const dec = jwtDecode(newAccess);
      setAuth(a => ({
        ...a,
        isAuthenticated: true,
        role: extractRole(dec),
        username: dec.sub,
        accessToken: newAccess,
        refreshToken: newRefresh
      }));

      onRefreshed(newAccess);
      return true;
    } catch {
      clearTokens();
      setAuth(a => ({
        ...a,
        isAuthenticated: false,
        accessToken: null,
        refreshToken: null
      }));
      onRefreshed(null);
      return false;
    } finally {
      isRefreshing.current = false;
    }
  };

  // ------------- IMPORTANTE: versión correcta y con throttle -------------
  const computeCartCount = async () => {
    // si no estás autenticado, no pegues al backend
    if (!auth.isAuthenticated) return 0;

    const now = Date.now();
    if (cartFetching.current || now - lastCartFetch.current < CART_TTL_MS) {
      return lastCartValue.current; // devolvemos el cache
    }

    cartFetching.current = true;
    try {
      // 👉 getCartCountSafe YA devuelve un número (no un axios response)
      const count = await getCartCountSafe();
      if (Number.isFinite(count)) {
        lastCartValue.current = count;
        lastCartFetch.current = Date.now();
        return count;
      }
    } catch (e) {
      // seguimos a fallback
    } finally {
      cartFetching.current = false;
    }

    // Fallback: /cart y sumar quantities (única llamada, sin encadenar)
    try {
      const full = await getCart(); // axios response
      const items = full?.data?.items ?? [];
      const count = items.reduce((acc, it) => acc + (it?.quantity ?? it?.cantidad ?? 1), 0);
      lastCartValue.current = count;
      lastCartFetch.current = Date.now();
      return count;
    } catch {
      return 0;
    }
  };
  // -----------------------------------------------------------------------

  const refreshCartCount = async () => {
    try {
      if (!auth.isAuthenticated) {
        setAuth(a => ({ ...a, cartItemCount: 0 }));
        return;
      }
      const count = await computeCartCount();
      setAuth(a => ({ ...a, cartItemCount: count }));
    } catch (e) {
      console.error('Error al cargar contador de carrito:', e);
      setAuth(a => ({ ...a, cartItemCount: 0 }));
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const accessToken  = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      const finishAnon = () => setAuth(a => ({ ...a, isAuthLoading: false, isAuthenticated: false }));

      if (!refreshToken) {
        clearTokens();
        return finishAnon();
      }

      if (!accessToken) {
        const ok = await tryRefreshToken(refreshToken);
        if (!ok) return finishAnon();
        try {
          const count = await computeCartCount();
          setAuth(a => ({ ...a, cartItemCount: count, isAuthLoading: false }));
        } catch {
          setAuth(a => ({ ...a, cartItemCount: 0, isAuthLoading: false }));
        }
        return;
      }

      try {
        const dec = jwtDecode(accessToken);
        const expired = dec.exp < Date.now() / 1000;
        if (expired) {
          const ok = await tryRefreshToken(refreshToken);
          if (!ok) return finishAnon();
        } else {
          setAuthHeader(accessToken);
          setAuth(a => ({
            ...a,
            isAuthenticated: true,
            role: extractRole(dec),
            username: dec.sub,
            accessToken,
            refreshToken
          }));
        }
        try {
          const count = await computeCartCount();
          setAuth(a => ({ ...a, cartItemCount: count, isAuthLoading: false }));
        } catch {
          setAuth(a => ({ ...a, cartItemCount: 0, isAuthLoading: false }));
        }
      } catch {
        const ok = await tryRefreshToken(refreshToken);
        if (!ok) return finishAnon();
        try {
          const count = await computeCartCount();
          setAuth(a => ({ ...a, cartItemCount: count, isAuthLoading: false }));
        } catch {
          setAuth(a => ({ ...a, cartItemCount: 0, isAuthLoading: false }));
        }
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      response => response,
      async error => {
        const status = error?.response?.status;
        const original = error?.config;
        if ((status === 401 || status === 403) && original && !original._retry) {
          original._retry = true;
          const rt = localStorage.getItem('refreshToken');
          const ok = await tryRefreshToken(rt);
          if (ok) {
            original.headers = original.headers || {};
            original.headers['Authorization'] = axios.defaults.headers.common['Authorization'];
            return axios(original);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySession = async (accessToken, refreshToken) => {
    persistTokens(accessToken, refreshToken);
    setAuthHeader(accessToken);
    const dec = jwtDecode(accessToken);
    setAuth(a => ({
      ...a,
      isAuthenticated: true,
      role: extractRole(dec),
      username: dec.sub,
      accessToken,
      refreshToken,
      isAuthLoading: false
    }));
    await refreshCartCount();
    return extractRole(dec);
  };

  const login = async (nombreUsuario, clave) => {
    const { data } = await API.post('/login', { nombreUsuario, clave });
    return applySession(data.accessToken, data.refreshToken);
  };

  const loginWithGoogle = async (credential, nonce, { createIfMissing = true } = {}) => {
    const { data } = await API.post('/oauth/google/login', { credential, nonce, createIfMissing });
    const role = await applySession(data.accessToken, data.refreshToken);
    return {
      role,
      isNewUser: !!data.isNewUser,
      linked: !!data.linked,
      username: jwtDecode(data.accessToken).sub
    };
  };

  // Vincular Google a una cuenta ya registrada: exige su contraseña, igual que el login.
  const linkGoogle = async ({ credential, nonce, nombreUsuario, clave }) => {
    const { data } = await API.post('/oauth/google/link', {
      credential,
      nonce,
      nombreUsuario,
      clave
    });
    const role = await applySession(data.accessToken, data.refreshToken);
    return {
      role,
      linked: true,
      username: jwtDecode(data.accessToken).sub
    };
  };

  const logout = () => {
    clearTokens();
    setAuth({
      isAuthenticated: false,
      role: null,
      username: null,
      accessToken: null,
      refreshToken: null,
      isAuthLoading: false,
      cartItemCount: 0
    });
  };

  return (
    <AuthContext.Provider value={{
      ...auth,
      authToken: auth.accessToken,
      login,
      loginWithGoogle,
      linkGoogle,
      logout,
      refreshCartCount
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

